import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "./useTenant";
import {
  ORDERS_WITH_LEGS_SELECT,
  ORDERS_WITH_LEGS_VIEW,
  hydrateLegRelations,
  mapLegRowToOrder,
  mapAndHydrateLegRows,
} from "@/lib/orders-with-legs";

/**
 * Loads a single leg + its sibling legs for the driver detail page.
 * `legId` matches the aliased `id` field on rows from `orders_with_legs`
 * (i.e. the underlying `leg_id`).
 */
export function useDriverAssignmentDetail(legId: string | undefined) {
  const { tenantId } = useTenant();

  const legQuery = useQuery({
    queryKey: ["driver-assignment-detail", legId, tenantId],
    queryFn: async () => {
      if (!legId || !tenantId) return null;
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDERS_WITH_LEGS_SELECT)
        .eq("tenant_id", tenantId)
        .eq("leg_id", legId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const [hydrated] = await hydrateLegRelations([mapLegRowToOrder(data)]);
      return hydrated;
    },
    enabled: !!legId && !!tenantId,
  });

  const parentOrderId = (legQuery.data as any)?.parent_order_id as string | undefined;

  const siblingsQuery = useQuery({
    queryKey: ["driver-assignment-siblings", parentOrderId, tenantId],
    queryFn: async () => {
      if (!parentOrderId || !tenantId) return [];
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDERS_WITH_LEGS_SELECT)
        .eq("tenant_id", tenantId)
        .eq("order_id", parentOrderId)
        .order("leg_sequence", { ascending: true });
      if (error) throw error;
      return mapAndHydrateLegRows(data);
    },
    enabled: !!parentOrderId && !!tenantId,
  });

  return {
    leg: legQuery.data ?? null,
    siblings: siblingsQuery.data ?? [],
    isLoading: legQuery.isLoading || siblingsQuery.isLoading,
  };
}